import { useNavigate } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatNumber, localiseDigits } from '@bookingdei/i18n/format-number';
import { dayPeriod, formatDate } from '@bookingdei/i18n/format-date';
import { statusLabelKeyOrNull } from '@bookingdei/i18n/status-label';
import { t as translate } from '@bookingdei/i18n/translate';
import type { Lang } from '@bookingdei/i18n/translate';
import { useLanguage } from '../../shell/language-context';
import { NavIcon } from '../../shell/nav-icon';
import { bookingToneColors } from '../../shell/booking-tone';
import { HomeIcon } from './home-icons';
import { addDays, branchLabel, byStart, needsAction } from './home-model';
import { formatPhone } from '@bookingdei/i18n/phone';
import { phonePlan } from '../../theme/phone-plan';
import type { MerchantBooking } from '../../mock/seeds/schema-types';

/** Clock digits and the period word, stacked like the design (9:15 / AM · ৯:১৫ / সকাল). */
function clockParts(lang: Lang, iso: string | undefined) {
  if (!iso) return { clock: '—', period: '' };
  const d = new Date(iso);
  const h12 = d.getHours() % 12 === 0 ? 12 : d.getHours() % 12;
  return {
    clock: formatMessage(lang, 'format.clock.hour_minute', {
      h: localiseDigits(lang, h12),
      m: localiseDigits(lang, String(d.getMinutes()).padStart(2, '0')),
    }),
    period: translate(lang, `format.clock.period.${dayPeriod(d.getHours())}`),
  };
}

const UPCOMING_ROWS = 5;

export function scheduleDayLabel(lang: Lang, offset: number, now: Date, t: (k: 'dc71.schedule.today' | 'dc71.schedule.tomorrow') => string): string {
  if (offset === 0) return t('dc71.schedule.today');
  if (offset === 1) return t('dc71.schedule.tomorrow');
  return formatDate(lang, addDays(now, offset), { year: false });
}

// Today's schedule (M:520-589): per-business tabs (All + one per business, with day
// counts) when the merchant holds more than one business, a day stepper, the "N
// bookings · N need you" line and a timeline of rows — clock, status dot, service +
// business tag, customer · phone, status chip and "Review" on rows that wait on the
// merchant. A row opens the booking in Bookings (`?b=`, DC72 drawer). Reads
// GET /merchant/bookings?business_id=all&from=&to= (API-95).
export function HomeSchedule({
  rows,
  businesses,
  tab,
  onTab,
  dayOffset,
  onDay,
  now,
  onNewBooking,
  serviceName,
  upcoming = [],
}: {
  rows: readonly MerchantBooking[];
  businesses: readonly { id: string; name: string }[];
  tab: string;
  onTab: (id: string) => void;
  dayOffset: number;
  onDay: (offset: number) => void;
  now: Date;
  /** Absent in a team member's session (the walk-in wizard needs the merchant-only catalog). */
  onNewBooking?: () => void;
  /** The booked service in the UI language (catalog name, else the booking snapshot). */
  serviceName: (b: MerchantBooking) => string;
  /** Live bookings after today (API-95 `tab=upcoming`), soonest first. */
  upcoming?: readonly MerchantBooking[];
}) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const multi = businesses.length > 1;
  const visible = rows.filter((b) => tab === 'all' || b.business_id === tab).sort(byStart);
  const need = visible.filter(needsAction).length;
  // An empty today still shows what is next, so the merchant sees their coming bookings
  // without opening Bookings (PRD §7.3.3 Home).
  const next =
    dayOffset === 0 && visible.length === 0
      ? upcoming.filter((b) => tab === 'all' || b.business_id === tab).slice(0, UPCOMING_ROWS)
      : [];
  const dayOf = (iso: string | undefined) => {
    if (!iso) return '';
    const d = new Date(iso);
    const offset = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86_400_000);
    return scheduleDayLabel(lang, offset, now, t);
  };
  const nameOf = (id: string) => branchLabel(businesses.find((b) => b.id === id)?.name ?? '');
  const tabs = [{ id: 'all', label: t('dc71.schedule.all') }, ...businesses.map((b) => ({ id: b.id, label: branchLabel(b.name) }))];

  const count = formatMessage(lang, 'dc71.schedule.meta', { n: visible.length });
  const meta = visible.length ? (need ? formatMessage(lang, 'dc71.schedule.meta_need', { count, n: need }) : count) : null;

  return (
    <section className="hm-card" aria-labelledby="hm-sched-title">
      {multi && (
        <div className="hm-tabs" role="tablist" aria-label={t('dc71.schedule.tabs')}>
          {tabs.map((tb) => {
            const on = tb.id === tab;
            const n = tb.id === 'all' ? rows.length : rows.filter((b) => b.business_id === tb.id).length;
            return (
              <button
                key={tb.id}
                type="button"
                role="tab"
                data-sched-tab={tb.id}
                aria-selected={on}
                className={on ? 'hm-tab is-on' : 'hm-tab'}
                onClick={() => onTab(tb.id)}
              >
                <span>{tb.label}</span>
                <span className="hm-tab__count">{formatNumber(lang, n)}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="hm-card__head hm-sched__head">
        <h3 id="hm-sched-title" className="hm-card__title hm-nowrap">
          {t('dc71.schedule.title')}
        </h3>
        <div className="hm-stepper">
          <button type="button" className="hm-stepper__btn" data-day="prev" aria-label={t('dc71.schedule.prev_day')} onClick={() => onDay(dayOffset - 1)}>
            <HomeIcon name="chevLeft" size={16} />
          </button>
          <span className="hm-stepper__label">{scheduleDayLabel(lang, dayOffset, now, t)}</span>
          <button type="button" className="hm-stepper__btn" data-day="next" aria-label={t('dc71.schedule.next_day')} onClick={() => onDay(dayOffset + 1)}>
            <HomeIcon name="chevRight" size={16} />
          </button>
        </div>
        <span className="hm-push hm-sched__meta">
          {meta && <span className="hm-sched__count">{meta}</span>}
          <button type="button" className="hm-link" onClick={() => navigate('/bookings')}>
            {t('dc71.schedule.see_all')} →
          </button>
        </span>
      </div>
      <div className="hm-sched__rows">
        {visible.map((b) => row(b))}
        {visible.length === 0 && (
          <div className="hm-sched__empty">
            <span className="hm-sched__empty-text">{dayOffset === 0 ? t('dc71.schedule.empty') : t('dc71.schedule.empty_day')}</span>
            {onNewBooking && (
              <button type="button" className="hm-pill hm-pill--sm" onClick={onNewBooking}>
                <NavIcon name="plus" size={16} strokeWidth={1.75} />
                <span>{t('dc71.schedule.new_booking')}</span>
              </button>
            )}
          </div>
        )}
        {next.length > 0 && (
          <>
            <h4 className="hm-sched__sub">{t('dc71.schedule.coming_up')}</h4>
            {next.map((b) => row(b, true))}
          </>
        )}
      </div>
    </section>
  );

  // One timeline row; a "coming up" row leads with its day instead of the period word.
  function row(b: MerchantBooking, withDay = false) {
          const { clock, period } = clockParts(lang, b.starts_at);
          const tone = bookingToneColors(b.status);
          const statusKey =
            b.status === 'payment_verification'
              ? 'dc71.schedule.status.payment_verification'
              : b.status === 'pending'
                ? 'dc71.schedule.status.pending'
                : statusLabelKeyOrNull('booking_status', b.status);
          const phone = b.hidden ? '•• ••• •••' : formatPhone(b.customer_phone, phonePlan);
          const who = b.hidden ? '••••••••' : (b.customer_name ?? t('dc71.schedule.walk_in'));
          return (
            <button
              key={b.id}
              type="button"
              className="hm-srow"
              data-booking={b.code}
              aria-label={formatMessage(lang, 'dc71.schedule.open', { code: b.code })}
              onClick={() => navigate(`/bookings?tab=all&b=${encodeURIComponent(b.code)}`)}
            >
              <span className="hm-srow__time">
                <span className="hm-srow__clock">{clock}</span>
                <span className="hm-srow__mer">{period}</span>
              </span>
              <span className="hm-srow__rail">
                <span className="hm-srow__line" />
                <span className="hm-srow__dot" style={{ background: tone.fg }} />
              </span>
              <span className="hm-srow__body">
                <span className="hm-srow__top">
                  <span className="hm-srow__svc">{serviceName(b)}</span>
                  {withDay && <span className="hm-srow__tag">{dayOf(b.starts_at)}</span>}
                  {multi && tab === 'all' && <span className="hm-srow__tag">{nameOf(b.business_id)}</span>}
                </span>
                <span className={b.hidden ? 'hm-srow__line2 hm-blur' : 'hm-srow__line2'}>
                  {who} · {phone}
                </span>
              </span>
              <span className="hm-srow__end">
                <span className="hm-srow__status" style={{ background: tone.bg, color: tone.fg }}>
                  {statusKey ? t(statusKey) : b.status}
                </span>
                {needsAction(b) && <span className="hm-srow__act">{t('dc71.schedule.review')}</span>}
                <span className="hm-srow__chev">
                  <HomeIcon name="chevRight" size={16} />
                </span>
              </span>
            </button>
          );
  }
}
